import Seller from "./schema/Seller";
import Address from "./schema/Address";
import Contact from "./schema/Contact";
import constants from "../config/constants";
import intlConstants from "../config/intlConstants";
import CustomercareGps from "./schema/CustomercareGps";
import Warehouse from "./schema/Warehouse";
import { createSellerService } from "../components/Integrations/serviceBff";
import SellerServiceFieldMap from "../constants/SellerServiceFieldMap";
import Document from "./schema/Document";

export const createSeller = async (
  country,
  formValues,
  documents,
  registrationCountry,
  countryOfSelling,
  formConfig
) => {
  const SellerData = new Seller();
  const isInternational = isIntlSeller(registrationCountry);
  const formData = getPreparedFormData(country, formValues, registrationCountry, countryOfSelling, isInternational);
  const sellerAddress = isInternational
    ? new Address(
        getFormattedIntlAddress(formData.addressHeadOffice, formData.municipalHeadOffice?.value),
        null,
        registrationCountry?.code
      )
    : new Address(formData.addressHeadOffice, formData.municipalHeadOffice, country);
  const contactAdmin = getContactAdmin(formData);
  const contactPersonInCharge = getContactPersonInCharge(formData);
  const selectedCountry = country === "INTL" ? registrationCountry?.code : country;
  const shipmentWarehouse = getShipmentWarehouse(selectedCountry, formData, isInternational);
  const returnWarehouse = getReturnWarehouse(selectedCountry, formData, isInternational, countryOfSelling);
  const formDocuments = getDocuments(country, documents);

  SellerData.setBasicInfo(formData);
  SellerData.setBusinessInfo(formData, isInternational, countryOfSelling);
  SellerData.setBankInfo(formData);
  SellerData.setDeclarationsInfo(formData);
  SellerData.setAddress(sellerAddress.getData());
  SellerData.setContacts(contactAdmin.getData());
  SellerData.setContacts(contactPersonInCharge.getData());
  SellerData.setWarehouses(shipmentWarehouse.getData());
  SellerData.setWarehouses(returnWarehouse.getData());
  SellerData.setDocuments(formDocuments);

  const sellerPayload = SellerData.getData();

  const serviceResponse = await createSellerService({ sellerData: sellerPayload });
  const response = prepareResponse(serviceResponse, formConfig);

  return response;
};

function isIntlSeller(registrationCountry) {
  const intlCountries = [intlConstants.VALID_COUNTRIES_INTL.US.name, intlConstants.VALID_COUNTRIES_INTL.CN.name];
  return intlCountries.includes(registrationCountry?.name);
}

function getFormattedIntlAddress(addressHeadOffice, municipalHeadOffice) {
  return [addressHeadOffice, municipalHeadOffice].filter(Boolean).join("||");
}

const prepareResponse = (serviceResponse, formConfig) => {
  let response = {
    status: "success",
    errors: {},
    message: ""
  };

  let errorList = serviceResponse.data?.errors;

  if (!Array.isArray(errorList) && typeof errorList === "object") {
    errorList = [errorList];
  }

  if (errorList !== undefined && errorList.length) {
    response.status = "error";
    response.message = "Errors in form.";

    response.errors = errorList[0]?.detail?.map(error => {
      const fieldName = SellerServiceFieldMap[`${error.target}.${error.property}`]
        ? SellerServiceFieldMap[`${error.target}.${error.property}`]
        : error.property;

      const formSection = getFieldSectionAndSubSection(fieldName, formConfig);

      if (Object.keys(formSection).length === 0) {
        return;
      }

      return {
        path: error.target,
        field: fieldName ? fieldName : "common_error",
        errorMessage: error.message,
        section: formSection
      };
    });

    response.errors = response.errors ?? [];
    response.errors = response.errors.filter(error => error !== undefined);
  }

  if (serviceResponse.status < 200 || serviceResponse.status > 206) {
    response.status = "error";
  }

  return response;
};

const getFieldSectionAndSubSection = (fieldName, formConfig) => {
  let location = {};

  if (!fieldName) {
    location.section = 1;
    return location;
  }

  formConfig.forEach(section => {
    if (section.fields && fieldExist(fieldName, section.fields)) {
      location.section = section.section.id;
      return location;
    } else if (section.subSections) {
      section.subSections.forEach(subSection => {
        if (subSection.fields && fieldExist(fieldName, subSection.fields)) {
          location.section = section.section.id;
          location.subSection = subSection.id;
          return location;
        }
      });
    }
  });

  return location;
};

const fieldExist = (fieldName, fields) => {
  let fieldExist = false;
  fields.forEach(field => {
    if (field.name === fieldName) {
      fieldExist = true;
    }
  });

  return fieldExist;
};

const getDocuments = (country, formData) => {
  const documents = [];
  Object.entries(constants.DOCUMENTS_PER_COUNTRY[country]).forEach(([documentIdentifier, documentType]) => {
    if (!formData[documentIdentifier]) {
      return;
    }
    const document = getSingleDocument(documentType, formData[documentIdentifier]);
    return documents.push(document.getData());
  });

  return documents;
};

const getSingleDocument = (documentType, formData) => {
  const { originalFileName, fileName, url, mime, size } = formData;
  return new Document(originalFileName, fileName, url, mime, size, documentType);
};

const getReturnWarehouse = (country, formData, isInternational, countryOfSelling) => {
  const shipmentAddress = isInternational
    ? new Address(
        getFormattedIntlAddress(formData.addressReturnWarehouse, formData.Address2codeReturnWarehouse),
        null,
        countryOfSelling
      )
    : new Address(formData.addressReturnWarehouse, formData.municipalReturnWarehouse, country);
  const gps = new CustomercareGps(
    formData.coordinatesReturnWarehouse?.lat ?? "0.0",
    formData.coordinatesReturnWarehouse?.lng ?? "0.0"
  );

  return new Warehouse(
    "Return",
    formData[constants.INPUT_ID_RETURN_WAREHOUSE_CONTACT_NAME],
    formData[constants.INPUT_ID_RETURN_WAREHOUSE_CONTACT_EMAIL],
    formData[constants.INPUT_ID_RETURN_WAREHOUSE_CONTACT_PHONE],
    shipmentAddress.getData(),
    gps.getData()
  );
};

const getShipmentWarehouse = (country, formData, isInternational) => {
  const shipmentAddress = isInternational
    ? new Address(
        getFormattedIntlAddress(formData.addressShipmentWarehouse, formData.municipalShipmentWarehouse?.value),
        null,
        country
      )
    : new Address(formData.addressShipmentWarehouse, formData.municipalShipmentWarehouse, country);
  const gps = new CustomercareGps(
    formData.coordinatesShipmentWarehouse?.lat ?? "0.0",
    formData.coordinatesShipmentWarehouse?.lng ?? "0.0"
  );

  return new Warehouse(
    "Shipment",
    formData[constants.INPUT_ID_SHIPMENT_WAREHOUSE_CONTACT_NAME],
    formData[constants.INPUT_ID_SHIPMENT_WAREHOUSE_CONTACT_EMAIL],
    formData[constants.INPUT_ID_SHIPMENT_WAREHOUSE_CONTACT_PHONE],
    shipmentAddress.getData(),
    gps.getData()
  );
};

const getContactPersonInCharge = formData => {
  return new Contact(
    "PersonInCharge",
    formData[constants.INPUT_ID_LEGAL_REPRESENTATIVE_DOCUMENT_NUMBER],
    formData[constants.INPUT_ID_LEGAL_REPRESENTATIVE_NAME],
    formData[constants.INPUT_ID_LEGAL_REPRESENTATIVE_LASTNAME],
    formData[constants.INPUT_ID_LEGAL_REPRESENTATIVE_DOCUMENT_TYPE] ?? "RUT",
    formData[constants.INPUT_ID_LEGAL_REPRESENTATIVE_EMAIL],
    formData[constants.INPUT_ID_LEGAL_REPRESENTATIVE_PHONE],
    formData[constants.INPUT_ID_DOCUMENT_ISSUING_COUNTRY]?.value ?? ""
  );
};

const getContactAdmin = formData => {
  const adminKeys = [
    constants.INPUT_ID_ADMIN_DOCUMENT_NUMBER,
    constants.INPUT_ID_ADMIN_FIRST_NAME,
    constants.INPUT_ID_ADMIN_LAST_NAME,
    constants.INPUT_ID_ADMIN_EMAIL,
    constants.INPUT_ID_ADMIN_PHONE
  ];

  if (!adminKeys.every(key => key in formData)) {
    return new Contact(
      "Admin",
      formData[constants.INPUT_ID_LEGAL_REPRESENTATIVE_DOCUMENT_NUMBER],
      formData[constants.INPUT_ID_LEGAL_REPRESENTATIVE_NAME],
      formData[constants.INPUT_ID_LEGAL_REPRESENTATIVE_LASTNAME],
      formData[constants.INPUT_ID_LEGAL_REPRESENTATIVE_DOCUMENT_TYPE] ?? "RUT",
      formData[constants.INPUT_ID_LEGAL_REPRESENTATIVE_EMAIL],
      formData[constants.INPUT_ID_LEGAL_REPRESENTATIVE_PHONE]
    );
  }

  return new Contact(
    "Admin",
    formData[constants.INPUT_ID_ADMIN_DOCUMENT_NUMBER],
    formData[constants.INPUT_ID_ADMIN_FIRST_NAME],
    formData[constants.INPUT_ID_ADMIN_LAST_NAME],
    formData[constants.INPUT_ID_ADMIN_DOCUMENT_TYPE] ?? "RUT",
    formData[constants.INPUT_ID_ADMIN_EMAIL],
    formData[constants.INPUT_ID_ADMIN_PHONE]
  );
};

const getPreparedFormData = (country, formValues, registrationCountry, countryOfSelling, isInternational) => {
  const selectedCountry = country === "INTL" ? countryOfSelling : country;
  formValues.countryName = getCountryCode(selectedCountry, country === "INTL");
  formValues.operatorCode = getOperatorCode(selectedCountry);
  formValues.geography = isInternational ? "international" : "national";
  formValues.businessRegistrationType = isInternational
    ? formValues.companyDocumentType
    : constants.DEFAULT_BUSINESS_REGISTRATION_TYPE[country];
  formValues.businessRegistrationCategory = isInternational
    ? "AA"
    : (formValues.businessRegistrationCategory ?? getBusinessRegistartionCategory(country));

  if (formValues.legalForm === undefined || (isInternational && countryOfSelling == intlConstants.COLOMBIA_CODE)) {
    formValues.legalForm = "legal_person";
  }

  if (formValues.conflictOfInterest === constants.NO) {
    formValues.personNameWithConflictOfInterest = "";
    formValues.typeOfConflict = "";
  }

  if (formValues.hasPepRelationship === constants.NO) {
    formValues.pepName = "";
    formValues.relationshipWithPep = "";
  }

  formValues.isAPes = formValues.isAPes === constants.YES ? true : false;
  formValues.personalInfoAuthorization = Boolean(formValues.personalInfoAuthorization);
  formValues.acceptSearchingCentralRisk = Boolean(formValues.acceptSearchingCentralRisk);

  formValues.confirmTerm = true;

  return formValues;
};

const getCountryCode = country => {
  return constants.VALID_COUNTRIES[country].countryCode ?? "";
};

const getOperatorCode = country => {
  return constants.VALID_COUNTRIES[country].operatorCode ?? "";
};

// eslint-disable-next-line no-unused-vars
const getBusinessRegistartionCategory = country => {
  return "";
};
